/** F6 Debrief: up to three auto-picked stories, the fixed six-slide deck (no editing), and the post-check release (screens §F6). */
import { useState } from "react";
import type { CohortBoard, RoomScreenShow, RoomScreenView } from "@bsim/contracts";
import { useRoute } from "../api/context";
import { Icon } from "../theme/icons";
import { GxCard, GxWaiting } from "../theme/primitives";
import { EmptyState } from "./EmptyState";
import { Link } from "../shell/history";
import { GxPill } from "../theme/GxPill";
import { Confirm } from "./BandDialogs";
import { GxPickCard } from "./PickCard";
import type { Act } from "./useAct";

/** The first read after a release builds the deck, which can take a minute. */
export const DECK_WAIT = "Preparing the deck… this can take up to a minute after a release";

const SLIDE_LABELS = { market: "Market", valueMoves: "What moved value", forecasts: "Forecasts", events: "Events", lookThrough: "Reported vs underlying", question: "Question" } as const;

export function DebriefTab({ cohortId, board, room, act, project }: {
  readonly cohortId: string; readonly board: CohortBoard; readonly room: RoomScreenView | undefined; readonly act: Act;
  readonly project: (show: RoomScreenShow) => void;
}) {
  const released = board.rounds.filter((r) => r.state === "released");
  const finalRound = board.rounds.at(-1);
  const [picked, setPicked] = useState<number | null>(null);
  const roundNo = picked ?? board.debriefPicksRoundNo ?? released.at(-1)?.roundNo ?? null;
  const round = released.find((r) => r.roundNo === roundNo) ?? null;
  const picks = useRoute("debriefPicks", round === null ? null : { cohortId, roundId: round.id });
  const deck = useRoute("debriefGet", roundNo === null ? null : { cohortId }, roundNo === null ? {} : { query: { roundNo } });
  const shown = room?.show;
  const postOpen = board.cohort.postAssessmentOpenedAt !== null;
  const post = board.prePost.post;
  const [confirmPost, setConfirmPost] = useState(false);

  // Nothing released: one card says so, not an empty picks card over an empty deck card.
  if (round === null && released.length === 0) return <div data-testid="studio.debrief"><GxCard title="Debrief" className="gx-fac-panel"><EmptyState icon="studio" title="No round has been released yet" action={<Link className="gx-btn gx-btn--secondary" href={`/f/${cohortId}`}>Back to the board</Link>}>The debrief picks and the six-slide deck appear here when you release round 1.</EmptyState></GxCard></div>;

  return (
    <div data-testid="studio.debrief">
      <GxCard title={`Debrief picks${roundNo === null ? "" : ` · Round ${roundNo}`}`} className="gx-fac-panel"
        aside={<span className="gx-fac-roundsel">
          {released.map((r) => <button key={r.id} type="button" className="gx-chip" aria-pressed={r.roundNo === roundNo} onClick={() => setPicked(r.roundNo)}>Round {r.roundNo}</button>)}
          <span className="gx-small gx-faint">Picked automatically</span>
        </span>}>
        {picks.data?.picks.map((p) => <GxPickCard key={p.pickId} pick={p} onScreen={shown?.kind === "pick" && shown.pickId === p.pickId} onProject={() => project({ kind: "pick", pickId: p.pickId })} />)}
        {picks.data !== undefined && picks.data.picks.length === 0 ? <EmptyState icon="check" title="No picks this round" /> : null}
      </GxCard>

      <GxCard title="Deck" className="gx-fac-panel" aside={<span className="gx-small gx-faint">Six slides, fixed order</span>}>
        {deck.data === undefined ? (roundNo !== null && deck.isPending ? <GxWaiting what="the deck" words={DECK_WAIT} testId="deck.loading" /> : <EmptyState icon="studio" title="The deck appears after a round is released" />) : (
          <ol className="gx-fac-deck" data-testid="deck">
            {deck.data.slides.map((s) => {
              const on = shown?.kind === "slide" && shown.slideNo === s.slideNo && shown.roundNo === deck.data.throughRoundNo;
              return (
                <li key={s.slideNo} className="gx-fac-slide" data-on-screen={on} data-testid={`slide.${s.slideNo}`}>
                  <div className="gx-fac-sn gx-num">{s.slideNo}</div>
                  <div className="gx-fac-st"><b>{s.title}</b><span className="gx-small gx-faint">{SLIDE_LABELS[s.kind]}</span></div>
                  {on ? <GxPill tone="gold">On screen</GxPill>
                    : <button type="button" className="gx-btn gx-btn--secondary" aria-label={`Project slide ${s.slideNo}`} onClick={() => project({ kind: "slide", roundNo: deck.data.throughRoundNo, slideNo: s.slideNo })}><Icon name="room" size={14} />Project</button>}
                </li>
              );
            })}
          </ol>
        )}
      </GxCard>

      {finalRound?.state === "released" ? (
        <GxCard title="Post-check" className="gx-fac-panel" aside={postOpen ? <GxPill tone="good">Open</GxPill> : null}>
          <div className="gx-card-b gx-fac-postrow">
            <span className="gx-muted">Sends the closing self-assessment (the post-check) to every participant's device.</span>
            <button type="button" className="gx-btn gx-btn--accent" disabled={postOpen} data-testid="studio.postOpen" onClick={() => setConfirmPost(true)}>{postOpen ? `Released · ${post.submitted} of ${post.submitted + post.inProgress + post.notStarted} done` : "Release the post-check"}</button>
          </div>
        </GxCard>
      ) : null}
      {confirmPost ? (
        <Confirm title="Release the post-check?" testId="dialog.postCheck" confirmLabel="Release the post-check" onClose={() => setConfirmPost(false)}
          lead="Every participant's device gets the closing self-assessment now. It cannot be taken back."
          onConfirm={async () => (await act("postAssessmentOpen", { cohortId }, undefined, { idempotent: true })).ok} />
      ) : null}
    </div>
  );
}
